import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import '../styles/notifications.css';

function formatTime(date) {
  const now      = new Date();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const time     = date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  if (date.toDateString() === now.toDateString())      return `Hoy ${time}`;
  if (date.toDateString() === tomorrow.toDateString()) return `Mañana ${time}`;
  return date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) + ` ${time}`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const { notifications, unreadCount, markAllRead, reminder, dismissReminder } = useNotifications();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Auto-dismiss reminder after 15s
  useEffect(() => {
    if (!reminder) return;
    const t = setTimeout(dismissReminder, 15000);
    return () => clearTimeout(t);
  }, [reminder, dismissReminder]);

  const handleBellClick = () => {
    const opening = !open;
    setOpen(opening);
    if (opening && unreadCount > 0) markAllRead();
  };

  const minsAway = reminder
    ? Math.max(0, Math.round((reminder.time.getTime() - Date.now()) / 60000))
    : 0;
  const reminderLabel = minsAway === 0 ? '¡Ahora!' : `En ${minsAway} min`;

  return (
    <>
      <div className="notif-wrap" ref={wrapRef}>
        <button className="notif-bell-btn" onClick={handleBellClick} aria-label="Notificaciones">
          🔔
          {unreadCount > 0 && (
            <span className="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </button>

        {open && (
          <div className="notif-panel">
            <div className="notif-panel-head">
              <span>Notificaciones</span>
              {notifications.length > 0 && (
                <button className="notif-read-btn" onClick={markAllRead}>✓ Leídas</button>
              )}
            </div>

            {notifications.length === 0 ? (
              <div className="notif-empty">Sin notificaciones para hoy</div>
            ) : (
              <div className="notif-list">
                {notifications.map(n => (
                  <div
                    key={n.id}
                    className={`notif-item${n.type === 'birthday' ? ' notif-item--birthday' : ''}`}
                    onClick={() => { setOpen(false); navigate(n.url); }}
                  >
                    <span className="notif-item-icon">{n.icon}</span>
                    <div className="notif-item-body">
                      <div className="notif-item-dog">{n.dogName}</div>
                      <div className="notif-item-meta">
                        {n.type === 'birthday' ? n.label : `${n.label} · ${formatTime(n.time)}`}
                      </div>
                    </div>
                    <span className="notif-item-arrow">›</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {reminder && (
        <div
          className="notif-reminder"
          onClick={() => { dismissReminder(); navigate(reminder.url); }}
        >
          <span className="notif-reminder-ic">⏰</span>
          <div className="notif-reminder-body">
            <strong>{reminderLabel}</strong>
            <span>{reminder.icon} {reminder.dogName} — {reminder.label}</span>
          </div>
          <button
            className="notif-reminder-close"
            onClick={e => { e.stopPropagation(); dismissReminder(); }}
          >✕</button>
        </div>
      )}
    </>
  );
}
